import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Image,
  Spinner,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { UserPlus } from "lucide-react";
import communityService from "../../../api/communityService";
import { getUser } from "../../../utils/auth";
import { toast } from "sonner";
import { FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import usePlan from "../../../hooks/usePlan";
import UserProfileModal from "../../community/components/user-profile-modal/UserProfileModal";
import RestrictionModal from "../../restriction-modals/RestrictionModal";
import { useTheme } from "../../../context/ThemeContext";

const FollowersModal = ({ show, onHide }) => {
  const { resolvedTheme } = useTheme();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]); // Add following list state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6, // Same limit for both APIs
    total: 0,
    total_pages: 0,
  });
  const [followingPagination, setFollowingPagination] = useState({
    page: 1,
    limit: 6, // Same limit as followers
    total: 0,
    total_pages: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // Unfollow modal state
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [userToUnfollow, setUserToUnfollow] = useState(null);
  const [unfollowLoading, setUnfollowLoading] = useState(false);

  // User Profile Modal state
  const [selectedProfileUser, setSelectedProfileUser] = useState(null);
  const [showProfileUserModal, setShowProfileUserModal] = useState(false);
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);

  // Get user from Redux store
  const user = useSelector((state) => state.auth?.user);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isFree } = usePlan();

  const currentUser = getUser();

  // Fetch followers
  const fetchFollowers = async (page = 1, append = false) => {
    try {
      if (!append) setLoading(true);

      const response = await communityService.getFollowers(
        currentUser.id,
        page,
        pagination.limit,
      );

      if (response?.data?.success) {
        const newFollowers = response.data.data?.followers || [];
        const paginationData = response.data.data?.pagination || {};

        setFollowers((prev) =>
          append ? [...prev, ...newFollowers] : newFollowers,
        );
        setPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching followers:", err);
      setError("Failed to load followers");
    } finally {
      if (!append) setLoading(false);
    }
  };

  // Fetch following list to check follow status with pagination
  const fetchFollowing = async (page = 1, append = false) => {
    try {
      const response = await communityService.getFollowing(
        currentUser.id,
        page,
        followingPagination.limit,
      );

      if (response?.data?.success) {
        const newFollowingUsers = response.data.data?.following || [];
        const paginationData = response.data.data?.pagination || {};

        setFollowing((prev) =>
          append ? [...prev, ...newFollowingUsers] : newFollowingUsers,
        );
        setFollowingPagination(paginationData);
      }
    } catch (err) {
      console.error("Error fetching following:", err);
    }
  };

  // Check if current user is following a specific follower
  const isFollowingUser = (userId) => {
    return following.some((user) => user.id === userId);
  };

  // Load more data for both APIs
  const loadMore = async () => {
    const canLoadMoreFollowers = pagination.page < pagination.total_pages;
    const canLoadMoreFollowing =
      followingPagination.page < followingPagination.total_pages;

    if ((canLoadMoreFollowers || canLoadMoreFollowing) && !loadingMore) {
      setLoadingMore(true);

      try {
        // Load more data for both APIs simultaneously
        const promises = [];

        if (canLoadMoreFollowers) {
          promises.push(fetchFollowers(pagination.page + 1, true));
        }
        if (canLoadMoreFollowing) {
          promises.push(fetchFollowing(followingPagination.page + 1, true));
        }

        await Promise.all(promises);
      } catch (error) {
        console.error("Error loading more data:", error);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  // Handle scroll to load more
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Load more when scrolled to bottom (with 20px threshold for better responsiveness)
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      const canLoadMoreFollowers = pagination.page < pagination.total_pages;
      const canLoadMoreFollowing =
        followingPagination.page < followingPagination.total_pages;

      if ((canLoadMoreFollowers || canLoadMoreFollowing) && !loadingMore) {
        loadMore();
      }
    }
  };

  // Follow/Unfollow user
  const handleFollowToggle = async (userId, isFollowing) => {
    if (isFollowing) {
      // If unfollowing, show confirmation modal
      const user = followers.find((f) => f.id === userId);
      setUserToUnfollow(user);
      setShowUnfollowModal(true);
    } else {
      // Free-user gate: free users cannot follow anyone — show upgrade modal
      if (isFree) {
        setShowPaywallSheet(true);
        return;
      }
      // If following, do it directly
      try {
        const response = await communityService.follow(userId);

        if (response?.data?.success) {
          // Add user to following list
          const userToAdd = followers.find((f) => f.id === userId);
          if (userToAdd) {
            setFollowing((prev) => [...prev, userToAdd]);
          }

          toast.success("Followed back successfully");
        }
      } catch (error) {
        console.error("Error following user:", error);
        toast.error("Failed to follow user");
      }
    }
  };

  // Confirm unfollow user
  const confirmUnfollow = async () => {
    if (!userToUnfollow) return;

    setUnfollowLoading(true);

    try {
      const response = await communityService.unfollow(userToUnfollow.id);

      if (response?.data?.success) {
        // Remove user from following list
        setFollowing((prev) =>
          prev.filter((user) => user.id !== userToUnfollow.id),
        );

        toast.success(
          `Unfollowed ${userToUnfollow.first_name} ${userToUnfollow.last_name} successfully`,
        );
        setShowUnfollowModal(false);
        setUserToUnfollow(null);
      }
    } catch (error) {
      console.error("Error unfollowing user:", error);
      toast.error("Failed to unfollow user");
    } finally {
      setUnfollowLoading(false);
    }
  };

  // Cancel unfollow
  const cancelUnfollow = () => {
    setShowUnfollowModal(false);
    setUserToUnfollow(null);
  };

  // Handle user profile click with subscription restriction
  const handleUserProfileClick = (user) => {
    // Don't open modal for own profile
    if (user.id === currentUser?.id) {
      return;
    }

    // Free users cannot open the profile modal at all — show the upgrade modal
    if (isFree) {
      setShowPaywallSheet(true);
      return;
    }

    // Set selected user and show modal
    setSelectedProfileUser(user);
    setShowProfileUserModal(true);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (show && currentUser?.id) {
      setFollowers([]);
      setFollowing([]);
      setError(null);
      setPagination((prev) => ({ ...prev, page: 1 }));
      setFollowingPagination((prev) => ({ ...prev, page: 1 }));
      fetchFollowers(1, false);
      fetchFollowing(1, false); // Fetch following list with pagination
    }
  }, [show, currentUser?.id]);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="md"
        backdrop="static"
        dialogClassName="custom-modal profile-other-details-modal"
      >
        <Modal.Header>
          <h5 className="modal-title">
            Followers{pagination.total > 0 ? ` (${pagination.total})` : ""}
          </h5>
          <Button className="close-btn" onClick={onHide}>
            <FiX size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: "400px", overflowY: "auto" }}
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : followers.length === 0 ? (
            <div className="text-center py-4">
              <UserPlus size={48} className="text-muted mb-3" />
              <h6>No followers yet</h6>
              <p className="text-muted small">
                When people follow you, they'll appear here.
              </p>
            </div>
          ) : (
            <>
              <ListGroup variant="flush" className="user-follow-list">
                {followers.map((follower) => (
                  <ListGroup.Item
                    key={follower.id}
                    className="px-0 py-2 border-0"
                    style={{
                      cursor:
                        follower.id !== currentUser?.id ? "pointer" : "default",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div
                        className="d-flex align-items-center"
                        onClick={() => handleUserProfileClick(follower)}
                        style={{
                          cursor:
                            follower.id !== currentUser?.id
                              ? "pointer"
                              : "default",
                          flex: 1,
                        }}
                      >
                        <Image
                          src={
                            follower.profile_photo_path
                              ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${follower.profile_photo_path}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  `${follower.first_name} ${follower.last_name}` ||
                                    "User",
                                )}&background=0195D3&color=fff&size=40`
                          }
                          alt={`${follower.first_name} ${follower.last_name}`}
                          roundedCircle
                          width={40}
                          height={40}
                          className="me-3"
                          style={{
                            objectFit: "cover",
                            cursor:
                              follower.id !== currentUser?.id
                                ? "pointer"
                                : "default",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              `${follower.first_name} ${follower.last_name}` ||
                                "User",
                            )}&background=0195D3&color=fff&size=40`;
                          }}
                        />
                        <div>
                          {/* <h6
                            className="mb-0 small"
                            style={{
                              color:
                                follower.id !== currentUser?.id
                                  ? "#0195D3"
                                  : "#333",
                              cursor:
                                follower.id !== currentUser?.id
                                  ? "pointer"
                                  : "default",
                            }}
                          >
                            {follower.first_name} {follower.last_name}
                          </h6> */}
                          <h6
                            className="mb-0 small"
                            style={{
                              color: "var(--mmp-text-primary)",
                              cursor:
                                follower.id !== currentUser?.id
                                  ? "pointer"
                                  : "default",
                              transition: "color 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (follower.id !== currentUser?.id) {
                                e.target.style.color = "var(--mmp-accent)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = "var(--mmp-text-primary)";
                            }}
                          >
                            {follower.first_name} {follower.last_name}
                          </h6>
                          <small className="text-muted">
                            @{follower.username}
                          </small>
                        </div>
                      </div>

                      {/* Follow Back/Unfollow Buttons */}
                      {follower.id !== currentUser?.id && (
                        <div className="d-flex gap-1">
                          {!isFollowingUser(follower.id) ? (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                handleFollowToggle(follower.id, false)
                              }
                              style={{ fontSize: "12px", padding: "4px 12px" }}
                            >
                              Follow Back
                            </Button>
                          ) : (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                handleFollowToggle(follower.id, true)
                              }
                              style={{ fontSize: "12px", padding: "4px 12px" }}
                            >
                              Unfollow
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Loading indicator when loading more */}
              {loadingMore && (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <small className="text-muted ms-2">Loading more...</small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Unfollow Confirmation Modal */}
      <Modal
        show={showUnfollowModal}
        onHide={cancelUnfollow}
        centered
        size="sm"
        backdrop="static"
        dialogClassName="custom-modal profile-other-details-modal unfollow-confirmation-modal"
      >
        {/* Custom backdrop with blur effect */}
        {showUnfollowModal && (
          <div
            className="modal-backdrop-blur"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor:
                resolvedTheme === "dark"
                  ? "rgba(11, 20, 24, 0.7)"
                  : "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 1040,
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            position: "relative",
            zIndex: 1050,
          }}
        >
          <Modal.Header>
            <h2 className="modal-title">Unfollow User</h2>
            <Button className="close-btn" onClick={cancelUnfollow}>
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            {userToUnfollow && (
              <div className="text-center">
                <Image
                  src={
                    userToUnfollow.profile_photo_path
                      ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${userToUnfollow.profile_photo_path}`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          `${userToUnfollow.first_name} ${userToUnfollow.last_name}` ||
                            "User",
                        )}&background=0195D3&color=fff&size=60`
                  }
                  alt={`${userToUnfollow.first_name} ${userToUnfollow.last_name}`}
                  roundedCircle
                  width={60}
                  height={60}
                  className="mb-2"
                  style={{ objectFit: "cover" }}
                />
                <h6 className="mb-1">
                  {userToUnfollow.first_name} {userToUnfollow.last_name}
                </h6>
                <p className="text-muted small mb-2">
                  @{userToUnfollow.username}
                </p>
                <p className="small">
                  Are you sure you want to unfollow this user?
                </p>
              </div>
            )}
            <div className="text-center mt-4">
              <Button
                size="sm"
                variant="outline-danger"
                onClick={confirmUnfollow}
                disabled={unfollowLoading}
                style={{ fontSize: "12px", padding: "4px 12px" }}
              >
                {unfollowLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Unfollowing...
                  </>
                ) : (
                  "Unfollow"
                )}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </Modal>

      {/* User Profile Modal */}
      <UserProfileModal
        show={showProfileUserModal}
        onHide={() => {
          setShowProfileUserModal(false);
          setSelectedProfileUser(null);
        }}
        userId={selectedProfileUser?.id}
        userName={`${selectedProfileUser?.first_name} ${selectedProfileUser?.last_name}`}
        userImage={selectedProfileUser?.profile_photo_path}
        userState={selectedProfileUser?.state}
      />

      {/* Subscription restriction modal */}
      <RestrictionModal
        isVisible={showPaywallSheet}
        onClose={() => setShowPaywallSheet(false)}
        eventSource="follow_limit"
        iconType="user-follow"
        heading="Want to follow or chat with this user?"
        subText="Upgrade to connect and build real relationships."
        infoText="Pro users can follow, chat, and connect with anyone in the community."
      />
    </>
  );
};

export default FollowersModal;
